import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortByName } from '../../../utils/sortByName';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { fetchGroups, Group } from '../../../api/groups';
import { fetchUserSchoolYears, UserSchoolYear } from '../../../api/school-year';

export type SchoolYear = {
  displayName: string;
  id: string;
} & UserSchoolYear;

export type MyGroupsContextValue = {
  groups?: Group[];
  onGroupsChange: React.Dispatch<React.SetStateAction<Group[] | undefined>>;
  schoolYears: SchoolYear[];
  selectedSchoolYear?: SchoolYear | undefined;
  onSchoolYearChange: React.Dispatch<React.SetStateAction<SchoolYear | undefined>>;
  groupsWithSchool: GroupsWithSchool;
  isLoadingGroups: boolean;
  fetchGroups: (selectedSchoolYear: SchoolYear) => Promise<void>;
};

export type GroupsWithSchool = { school: Group['school']; groups: Group[] }[];

const defaultMyGroupsContextValue = {
  schoolYears: [],
  onSchoolYearChange: () => {},
  onGroupsChange: () => {},
  groupsWithSchool: [],
  groups: [],
  isLoadingGroups: false,
  fetchGroups: async () => {},
};

export const MyGroupsContext = createContext<MyGroupsContextValue>(defaultMyGroupsContextValue);

const getTranslationKey = (schoolYear: UserSchoolYear) => {
  if (schoolYear.hasPassed) return 'COMPONENTS.SCHOOLYEAR_SELECTOR.PREVIOUS';
  if (schoolYear.isCurrent) return 'COMPONENTS.SCHOOLYEAR_SELECTOR.CURRENT';
  return 'COMPONENTS.SCHOOLYEAR_SELECTOR.NEXT';
};

export const sortGroups = (groups: Group[], language: string) => {
  return groups.sort(sortByName(language));
};

export default function MyGroupsContextProvider({ children }: { children: ReactNode }) {
  const {
    i18n: { language },
  } = useTranslation();
  const [groups, setGroups] = useState<Group[]>();
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState<SchoolYear>();
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  const handleError = useThrowAsyncError();
  const groupsWithSchool = useMemo(() => {
    const unOrderedGroupsWithSchool = (groups || []).reduce<GroupsWithSchool>((acc, group) => {
      const school = acc.find(x => x.school.id === group.school.id);

      if (school) {
        school.groups.push(group);
      } else {
        acc.push({
          school: group.school,
          groups: [group],
        });
      }

      return acc;
    }, []);

    return unOrderedGroupsWithSchool.sort((a, b) =>
      a.school.name.localeCompare(b.school.name, language, { sensitivity: 'base' }),
    );
  }, [groups, language]);

  useEffect(() => {
    const getUserSchoolYears = async () => {
      const userSchoolYears = await fetchUserSchoolYears();

      const enrichedUserSchoolYears: SchoolYear[] = userSchoolYears.map(schoolYear => ({
        ...schoolYear,
        displayName: getTranslationKey(schoolYear),
        id: `${schoolYear.startYear}-${schoolYear.endYear}`,
      }));

      setSchoolYears(enrichedUserSchoolYears);
    };

    getUserSchoolYears().catch(handleError);
  }, [handleError]);

  const getGroupsOfSchoolYear = useCallback(
    async (selectedSchoolYear: SchoolYear) => {
      setIsLoadingGroups(true);

      const fetchedGroups = await fetchGroups(selectedSchoolYear);
      const sortedGroups = sortGroups(fetchedGroups, language);

      setGroups(sortedGroups);
      setIsLoadingGroups(false);
    },
    [language, setGroups, setIsLoadingGroups],
  );

  useEffect(() => {
    if (!selectedSchoolYear) return;

    getGroupsOfSchoolYear(selectedSchoolYear).catch(handleError);
  }, [selectedSchoolYear, handleError, getGroupsOfSchoolYear]);

  return (
    <MyGroupsContext.Provider
      value={{
        onGroupsChange: setGroups,
        schoolYears,
        selectedSchoolYear,
        onSchoolYearChange: setSelectedSchoolYear,
        groupsWithSchool,
        groups,
        isLoadingGroups,
        fetchGroups: getGroupsOfSchoolYear,
      }}
    >
      {children}
    </MyGroupsContext.Provider>
  );
}
